import { Link } from 'react-router-dom';
import ModernNavigation from '@/components/ModernNavigation';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Bike, Star, ArrowRight } from 'lucide-react';

interface DestinationPageProps {
  title: string;
  subtitle: string;
  description: string;
  highlights: string[];
  image?: string;
  villages?: { name: string; path: string }[];
}

const DestinationPage = ({ 
  title, 
  subtitle, 
  description, 
  highlights,
  image,
  villages 
}: DestinationPageProps) => {
  return (
    <div className="min-h-screen bg-background">
      <ModernNavigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-50 to-white py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <MapPin className="w-4 h-4 mr-2" />
              Destination
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {title}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {subtitle}
            </p>
          </div>

          {image && (
            <div className="rounded-2xl overflow-hidden shadow-2xl mb-12">
              <img 
                src={image} 
                alt={title}
                className="w-full h-[400px] object-cover"
              />
            </div>
          )}

          <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              {description}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {highlights.map((highlight, index) => (
                <div key={index} className="flex items-start">
                  <Star className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                  <p className="text-gray-700">{highlight}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Villages Grid */}
          {villages && villages.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Découvrez nos villages
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {villages.map((village) => (
                  <Link key={village.path} to={village.path}>
                    <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                      <CardContent className="p-4 flex items-center justify-between">
                        <span className="font-medium text-gray-900">{village.name}</span>
                        <ArrowRight className="w-4 h-4 text-emerald-600" />
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* CTA Section */}
          <div className="text-center">
            <Link to="/reserver">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-3 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all">
                <Bike className="w-5 h-5 mr-2" />
                Louer un vélo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DestinationPage;